import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import type {
  ChatbotQuestion,
  ChatbotQuestionOption,
  ChatbotQuestionStepType,
  FreelancerType,
  ServiceCatalogBenefitTag,
  ServiceCatalogItem,
  ServiceCatalogLead,
  ServiceCatalogLeadStatus,
} from '@pigeons/shared-service-catalog';
import { serviceCatalogService } from '../services/serviceCatalogService';

type admin_tab = 'items' | 'freelancerTypes' | 'leads';

const get_error_message = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

const sort_by_order = <T extends { sort_order: number }>(items: T[]): T[] =>
  [...items].sort((a, b) => a.sort_order - b.sort_order);

const sort_by_question_order = (items: ChatbotQuestion[]): ChatbotQuestion[] =>
  [...items].sort((a, b) => a.question_order - b.question_order);

const chatbotQuestionDeleteToken = (itemId: string) => `chatbotQuestion-delete:${itemId}`;

/**
 * After a delete, the database's list decides which questions still exist. Local
 * copies are kept (they may hold unsaved edits) with the same goto/show_if cleanup
 * the database applied, so a later "save all" can't write the dangling reference back.
 */
const merge_after_question_delete = (
  local: ChatbotQuestion[],
  remaining: ChatbotQuestion[],
  deletedKey: string | undefined
): ChatbotQuestion[] => {
  const localById = new Map(local.map(question => [question.id, question]));
  return sort_by_question_order(
    remaining.map(stored => {
      const question = localById.get(stored.id) ?? stored;
      if (!deletedKey) return question;
      return {
        ...question,
        options:
          question.options?.map(option =>
            option.goto === deletedKey ? { ...option, goto: null } : option
          ) ?? null,
        show_if: question.show_if?.step_key === deletedKey ? null : question.show_if,
      };
    })
  );
};

const input_class =
  'rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm focus:border-primary focus:outline-none';
const label_class = 'flex flex-col gap-2 text-sm font-medium text-gray-700';

export default function ServiceCatalogAdmin() {
  const { t } = useTranslation('admin');
  const [activeTab, setActiveTab] = useState<admin_tab>('items');
  const [loading, setLoading] = useState(true);
  const [savingKeys, setSavingKeys] = useState<Record<string, boolean>>({});

  const [items, setItems] = useState<ServiceCatalogItem[]>([]);
  const [freelancerTypes, setFreelancerTypes] = useState<FreelancerType[]>([]);
  const [leads, setLeads] = useState<ServiceCatalogLead[]>([]);

  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  const [itemFreelancerTypeIds, setItemFreelancerTypeIds] = useState<Record<string, string[]>>({});
  const [itemBenefitTags, setItemBenefitTags] = useState<
    Record<string, ServiceCatalogBenefitTag[]>
  >({});
  const [itemChatbotQuestions, setItemChatbotQuestions] = useState<
    Record<string, ChatbotQuestion[]>
  >({});

  // Synchronous guard against a second delete starting before the disabled state renders.
  const deletingItemIdsRef = useRef<Set<string>>(new Set());

  const setSaving = (token: string, value: boolean) => {
    setSavingKeys(prev => ({ ...prev, [token]: value }));
  };

  const refreshAll = async () => {
    setLoading(true);
    try {
      const [nextItems, nextFreelancerTypes, nextLeads] = await Promise.all([
        serviceCatalogService.listServiceCatalogItems(),
        serviceCatalogService.listFreelancerTypes(),
        serviceCatalogService.listLeads(),
      ]);
      setItems(sort_by_order(nextItems));
      setFreelancerTypes(sort_by_order(nextFreelancerTypes));
      setLeads(nextLeads);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refreshAll();
  }, []);

  // -------------------- Freelancer types --------------------

  const handleAddFreelancerType = async () => {
    const token = 'freelancerType:add';
    setSaving(token, true);
    try {
      const item = await serviceCatalogService.createFreelancerType(freelancerTypes);
      setFreelancerTypes(prev => sort_by_order([...prev, item]));
    } catch (error) {
      toast.error(get_error_message(error, t('serviceCatalog.genericError')));
    } finally {
      setSaving(token, false);
    }
  };

  const saveFreelancerType = async (item: FreelancerType) => {
    const token = `freelancerType:${item.id}`;
    setSaving(token, true);
    try {
      const updated = await serviceCatalogService.updateFreelancerType(item.id, {
        name_fr: item.name_fr,
        name_en: item.name_en,
        emoji: item.emoji,
        color: item.color,
      });
      setFreelancerTypes(prev =>
        prev.map(existing => (existing.id === item.id ? updated : existing))
      );
      toast.success(t('serviceCatalog.saveSuccess'));
    } catch (error) {
      toast.error(get_error_message(error, t('serviceCatalog.genericError')));
    } finally {
      setSaving(token, false);
    }
  };

  const deleteFreelancerType = async (id: string) => {
    if (!window.confirm(t('serviceCatalog.confirmDelete'))) {
      return;
    }
    const token = `freelancerType-delete:${id}`;
    setSaving(token, true);
    try {
      await serviceCatalogService.deleteFreelancerType(id);
      setFreelancerTypes(prev => prev.filter(item => item.id !== id));
    } catch (error) {
      toast.error(get_error_message(error, t('serviceCatalog.genericError')));
    } finally {
      setSaving(token, false);
    }
  };

  const moveFreelancerType = async (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= freelancerTypes.length) return;
    const nextItems = [...freelancerTypes];
    [nextItems[index], nextItems[nextIndex]] = [nextItems[nextIndex], nextItems[index]];
    const normalized = nextItems.map((item, i) => ({ ...item, sort_order: (i + 1) * 10 }));
    setSaving(`freelancerType-reorder:${index}`, true);
    try {
      await serviceCatalogService.reorderFreelancerTypes(normalized);
      setFreelancerTypes(normalized);
    } catch (error) {
      toast.error(get_error_message(error, t('serviceCatalog.genericError')));
    } finally {
      setSaving(`freelancerType-reorder:${index}`, false);
    }
  };

  // -------------------- Items --------------------

  const handleAddItem = async () => {
    const token = 'item:add';
    setSaving(token, true);
    try {
      const item = await serviceCatalogService.createServiceCatalogItem(items);
      setItems(prev => sort_by_order([...prev, item]));
      toast.success(t('serviceCatalog.itemCreated'));
    } catch (error) {
      toast.error(get_error_message(error, t('serviceCatalog.genericError')));
    } finally {
      setSaving(token, false);
    }
  };

  // Saves the item's own fields plus every benefit tag and chatbot question
  // currently loaded for it, all under one "Save" action — there is
  // intentionally no per-field/per-row save button anywhere in this panel.
  // Saves child data (tags, questions) before the item row itself, and saves the item
  // last of all — including `active`. That way, if any child write fails, the item
  // (and its publish state) is left exactly as it was: never live with new copy paired
  // against stale-or-partially-saved tags/questions, and never newly activated with an
  // unconfirmed questionnaire. This is a partial mitigation, not a true transaction —
  // a failure partway through the tags or questions themselves can still leave those
  // two sets inconsistent with each other. A single transactional RPC (mirroring
  // set_service_catalog_item_freelancer_types) would close that gap; not done here to
  // keep this fix scoped.
  const saveAllForItem = async (item: ServiceCatalogItem) => {
    const token = `item:${item.id}`;
    setSaving(token, true);
    try {
      const benefitTags = itemBenefitTags[item.id] ?? [];
      if (benefitTags.length > 0) {
        const updatedTags = await Promise.all(
          benefitTags.map(tag =>
            serviceCatalogService.updateBenefitTag(tag.id, {
              label_fr: tag.label_fr,
              label_en: tag.label_en,
            })
          )
        );
        setItemBenefitTags(prev => ({ ...prev, [item.id]: updatedTags }));
      }

      const chatbotQuestions = itemChatbotQuestions[item.id] ?? [];
      if (chatbotQuestions.length > 0) {
        const updatedQuestions = await Promise.all(
          chatbotQuestions.map(question =>
            serviceCatalogService.updateChatbotQuestion(question.id, {
              question_text_fr: question.question_text_fr,
              question_text_en: question.question_text_en,
              placeholder_fr: question.placeholder_fr,
              placeholder_en: question.placeholder_en,
              step_type: question.step_type,
              optional: question.optional,
              options: question.options,
              show_if: question.show_if,
            })
          )
        );
        setItemChatbotQuestions(prev => ({ ...prev, [item.id]: updatedQuestions }));
      }

      const updatedItem = await serviceCatalogService.updateServiceCatalogItem(item.id, {
        slug: item.slug,
        badge_label_fr: item.badge_label_fr,
        badge_label_en: item.badge_label_en,
        category_fr: item.category_fr,
        category_en: item.category_en,
        name_fr: item.name_fr,
        name_en: item.name_en,
        short_description_fr: item.short_description_fr,
        short_description_en: item.short_description_en,
        full_description_fr: item.full_description_fr,
        full_description_en: item.full_description_en,
        chatbot_greeting_fr: item.chatbot_greeting_fr,
        chatbot_greeting_en: item.chatbot_greeting_en,
        chatbot_intro_fr: item.chatbot_intro_fr,
        chatbot_intro_en: item.chatbot_intro_en,
        active: item.active,
      });
      setItems(prev => prev.map(existing => (existing.id === item.id ? updatedItem : existing)));

      toast.success(t('serviceCatalog.saveSuccess'));
    } catch (error) {
      toast.error(get_error_message(error, t('serviceCatalog.genericError')));
    } finally {
      setSaving(token, false);
    }
  };

  const deleteItem = async (id: string) => {
    if (!window.confirm(t('serviceCatalog.confirmDelete'))) {
      return;
    }
    const token = `item-delete:${id}`;
    setSaving(token, true);
    try {
      await serviceCatalogService.deleteServiceCatalogItem(id);
      setItems(prev => prev.filter(item => item.id !== id));
      if (expandedItemId === id) setExpandedItemId(null);
    } catch (error) {
      toast.error(get_error_message(error, t('serviceCatalog.genericError')));
    } finally {
      setSaving(token, false);
    }
  };

  const moveItem = async (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= items.length) return;
    const nextItems = [...items];
    [nextItems[index], nextItems[nextIndex]] = [nextItems[nextIndex], nextItems[index]];
    const normalized = nextItems.map((item, i) => ({ ...item, sort_order: (i + 1) * 10 }));
    setSaving(`item-reorder:${index}`, true);
    try {
      await serviceCatalogService.reorderServiceCatalogItems(normalized);
      setItems(normalized);
    } catch (error) {
      toast.error(get_error_message(error, t('serviceCatalog.genericError')));
    } finally {
      setSaving(`item-reorder:${index}`, false);
    }
  };

  const toggleExpanded = async (item: ServiceCatalogItem) => {
    if (expandedItemId === item.id) {
      setExpandedItemId(null);
      return;
    }
    setExpandedItemId(item.id);
    if (
      itemFreelancerTypeIds[item.id] &&
      itemBenefitTags[item.id] &&
      itemChatbotQuestions[item.id]
    ) {
      return;
    }
    const token = `item-expand:${item.id}`;
    setSaving(token, true);
    try {
      const [freelancerTypeIds, benefitTags, chatbotQuestions] = await Promise.all([
        serviceCatalogService.listFreelancerTypeIdsForItem(item.id),
        serviceCatalogService.listBenefitTags(item.id),
        serviceCatalogService.listChatbotQuestions(item.id),
      ]);
      setItemFreelancerTypeIds(prev => ({ ...prev, [item.id]: freelancerTypeIds }));
      setItemBenefitTags(prev => ({ ...prev, [item.id]: sort_by_order(benefitTags) }));
      setItemChatbotQuestions(prev => ({
        ...prev,
        [item.id]: sort_by_question_order(chatbotQuestions),
      }));
    } catch (error) {
      toast.error(get_error_message(error, t('serviceCatalog.genericError')));
    } finally {
      setSaving(token, false);
    }
  };

  const toggleItemFreelancerType = async (itemId: string, freelancerTypeId: string) => {
    const current = itemFreelancerTypeIds[itemId] ?? [];
    const next = current.includes(freelancerTypeId)
      ? current.filter(id => id !== freelancerTypeId)
      : [...current, freelancerTypeId];

    const token = `item-freelancer-types:${itemId}`;
    setSaving(token, true);
    try {
      await serviceCatalogService.setFreelancerTypeIdsForItem(itemId, next);
      setItemFreelancerTypeIds(prev => ({ ...prev, [itemId]: next }));
    } catch (error) {
      toast.error(get_error_message(error, t('serviceCatalog.genericError')));
    } finally {
      setSaving(token, false);
    }
  };

  // -------------------- Benefit tags --------------------

  const addBenefitTag = async (itemId: string) => {
    const token = `benefitTag-add:${itemId}`;
    setSaving(token, true);
    try {
      const tag = await serviceCatalogService.createBenefitTag(
        itemId,
        itemBenefitTags[itemId] ?? []
      );
      setItemBenefitTags(prev => ({
        ...prev,
        [itemId]: sort_by_order([...(prev[itemId] ?? []), tag]),
      }));
    } catch (error) {
      toast.error(get_error_message(error, t('serviceCatalog.genericError')));
    } finally {
      setSaving(token, false);
    }
  };

  const deleteBenefitTag = async (itemId: string, tagId: string) => {
    const token = `benefitTag-delete:${tagId}`;
    setSaving(token, true);
    try {
      await serviceCatalogService.deleteBenefitTag(tagId);
      setItemBenefitTags(prev => ({
        ...prev,
        [itemId]: (prev[itemId] ?? []).filter(tag => tag.id !== tagId),
      }));
    } catch (error) {
      toast.error(get_error_message(error, t('serviceCatalog.genericError')));
    } finally {
      setSaving(token, false);
    }
  };

  // -------------------- Chatbot questions --------------------

  const addChatbotQuestion = async (itemId: string, stepType: ChatbotQuestionStepType) => {
    if (items.find(item => item.id === itemId)?.active) {
      toast.error(t('serviceCatalog.deactivateToEditQuestions'));
      return;
    }

    const token = `chatbotQuestion-add:${itemId}`;
    setSaving(token, true);
    try {
      const question = await serviceCatalogService.createChatbotQuestion(
        itemId,
        itemChatbotQuestions[itemId] ?? [],
        stepType
      );
      setItemChatbotQuestions(prev => ({
        ...prev,
        [itemId]: sort_by_question_order([...(prev[itemId] ?? []), question]),
      }));
    } catch (error) {
      toast.error(get_error_message(error, t('serviceCatalog.genericError')));
    } finally {
      setSaving(token, false);
    }
  };

  const deleteChatbotQuestion = async (itemId: string, questionId: string) => {
    // One token per item: while a delete is in flight, that item's delete and reorder
    // controls are disabled so no second request starts from a stale question list.
    const token = chatbotQuestionDeleteToken(itemId);
    if (deletingItemIdsRef.current.has(itemId)) return;
    deletingItemIdsRef.current.add(itemId);
    setSaving(token, true);
    const deletedKey = (itemChatbotQuestions[itemId] ?? []).find(
      question => question.id === questionId
    )?.client_key;
    try {
      // The database deletes the question and clears every goto/show_if that pointed
      // at it in one transaction, then returns the item's remaining questions, so
      // the public chatbot never follows a jump to a question that no longer exists.
      const remaining = await serviceCatalogService.deleteChatbotQuestion(questionId);
      setItemChatbotQuestions(prev => ({
        ...prev,
        [itemId]: merge_after_question_delete(prev[itemId] ?? [], remaining, deletedKey),
      }));
    } catch (error) {
      toast.error(get_error_message(error, t('serviceCatalog.genericError')));
    } finally {
      deletingItemIdsRef.current.delete(itemId);
      setSaving(token, false);
    }
  };

  const moveChatbotQuestion = async (itemId: string, index: number, direction: -1 | 1) => {
    const questions = itemChatbotQuestions[itemId] ?? [];
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= questions.length) return;
    const nextQuestions = [...questions];
    [nextQuestions[index], nextQuestions[nextIndex]] = [
      nextQuestions[nextIndex],
      nextQuestions[index],
    ];
    const normalized = nextQuestions.map((question, i) => ({
      ...question,
      question_order: (i + 1) * 10,
    }));
    const token = `chatbotQuestion-reorder:${itemId}:${index}`;
    setSaving(token, true);
    try {
      await serviceCatalogService.reorderChatbotQuestions(normalized);
      setItemChatbotQuestions(prev => ({ ...prev, [itemId]: normalized }));
    } catch (error) {
      toast.error(get_error_message(error, t('serviceCatalog.genericError')));
    } finally {
      setSaving(token, false);
    }
  };

  const updateQuestionOptions = (
    itemId: string,
    questionId: string,
    options: ChatbotQuestionOption[]
  ) => {
    setItemChatbotQuestions(prev => ({
      ...prev,
      [itemId]: (prev[itemId] ?? []).map(existing =>
        existing.id === questionId ? { ...existing, options } : existing
      ),
    }));
  };

  // -------------------- Leads --------------------

  const changeLeadStatus = async (lead: ServiceCatalogLead, status: ServiceCatalogLeadStatus) => {
    const token = `lead:${lead.id}`;
    setSaving(token, true);
    try {
      const updated = await serviceCatalogService.updateLeadStatus(lead.id, status);
      setLeads(prev => prev.map(existing => (existing.id === lead.id ? updated : existing)));
    } catch (error) {
      toast.error(get_error_message(error, t('serviceCatalog.genericError')));
    } finally {
      setSaving(token, false);
    }
  };

  const tabs: { key: admin_tab; label: string }[] = useMemo(
    () => [
      { key: 'items', label: t('serviceCatalog.tabs.items') },
      { key: 'freelancerTypes', label: t('serviceCatalog.tabs.freelancerTypes') },
      { key: 'leads', label: t('serviceCatalog.tabs.leads') },
    ],
    [t]
  );

  return (
    <div className="flex flex-col gap-8 px-4 py-6 md:px-6 lg:px-10">
      <div className="flex flex-col gap-3">
        <h1 className="text-4xl font-bold text-secondary">{t('serviceCatalog.title')}</h1>
        <p className="max-w-4xl text-base text-gray-500">{t('serviceCatalog.subtitle')}</p>
      </div>

      <div className="flex gap-2 border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`rounded-t-2xl px-4 py-2 text-sm font-semibold ${
              activeTab === tab.key
                ? 'border-b-2 border-primary text-primary'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="rounded-3xl border border-gray-200 bg-white px-6 py-10 text-sm text-gray-500 shadow-sm">
          {t('loading')}
        </div>
      ) : (
        <>
          {activeTab === 'items' && (
            <section className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm">
              <div className="mb-5 flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">
                    {t('serviceCatalog.tabs.items')}
                  </h2>
                  <p className="text-sm text-gray-500">{t('serviceCatalog.itemsSubtitle')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => void handleAddItem()}
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
                  disabled={Boolean(savingKeys['item:add'])}
                >
                  {savingKeys['item:add'] ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  {t('serviceCatalog.addItem')}
                </button>
              </div>

              <div className="flex flex-col gap-5">
                {items.map((item, index) => {
                  const isExpanded = expandedItemId === item.id;
                  const token = `item:${item.id}`;
                  const isSaving = Boolean(savingKeys[token]);
                  const freelancerTypeIds = itemFreelancerTypeIds[item.id] ?? [];
                  const benefitTags = itemBenefitTags[item.id] ?? [];
                  const chatbotQuestions = itemChatbotQuestions[item.id] ?? [];
                  const isDeletingQuestion = Boolean(
                    savingKeys[chatbotQuestionDeleteToken(item.id)]
                  );

                  return (
                    <article
                      key={item.id}
                      className="rounded-3xl border border-gray-200 bg-gray-50/60 p-4"
                    >
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => void toggleExpanded(item)}
                            className="rounded-full border border-gray-300 p-2 text-gray-600 hover:bg-white"
                          >
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </button>
                          <div>
                            <h3 className="text-base font-semibold text-gray-900">
                              {item.name_fr || item.name_en || item.slug}
                            </h3>
                            <p className="text-xs text-gray-500">
                              /{item.slug} ·{' '}
                              {item.active
                                ? t('serviceCatalog.active')
                                : t('serviceCatalog.inactive')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => void moveItem(index, -1)}
                            disabled={index === 0}
                            className="rounded-full border border-gray-300 p-2 text-gray-600 hover:bg-white disabled:opacity-40"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => void moveItem(index, 1)}
                            disabled={index === items.length - 1}
                            className="rounded-full border border-gray-300 p-2 text-gray-600 hover:bg-white disabled:opacity-40"
                          >
                            <ArrowDown className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => void deleteItem(item.id)}
                            className="rounded-full border border-red-200 p-2 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="mt-4 flex flex-col gap-5">
                          <div className="grid gap-3">
                            <label className="inline-flex items-center gap-3 text-sm font-medium text-gray-700">
                              <input
                                type="checkbox"
                                checked={item.active}
                                onChange={event =>
                                  setItems(prev =>
                                    prev.map(existing =>
                                      existing.id === item.id
                                        ? { ...existing, active: event.target.checked }
                                        : existing
                                    )
                                  )
                                }
                                className="h-4 w-4 rounded border-gray-300"
                              />
                              <span>{t('serviceCatalog.fields.active')}</span>
                            </label>

                            <label className={label_class}>
                              <span>{t('serviceCatalog.fields.slug')}</span>
                              <input
                                type="text"
                                value={item.slug}
                                onChange={event =>
                                  setItems(prev =>
                                    prev.map(existing =>
                                      existing.id === item.id
                                        ? { ...existing, slug: event.target.value }
                                        : existing
                                    )
                                  )
                                }
                                className={input_class}
                              />
                            </label>

                            <div className="grid gap-3 md:grid-cols-2">
                              <label className={label_class}>
                                <span>{t('serviceCatalog.fields.nameFr')}</span>
                                <input
                                  type="text"
                                  value={item.name_fr}
                                  onChange={event =>
                                    setItems(prev =>
                                      prev.map(existing =>
                                        existing.id === item.id
                                          ? { ...existing, name_fr: event.target.value }
                                          : existing
                                      )
                                    )
                                  }
                                  className={input_class}
                                />
                              </label>
                              <label className={label_class}>
                                <span>{t('serviceCatalog.fields.nameEn')}</span>
                                <input
                                  type="text"
                                  value={item.name_en}
                                  onChange={event =>
                                    setItems(prev =>
                                      prev.map(existing =>
                                        existing.id === item.id
                                          ? { ...existing, name_en: event.target.value }
                                          : existing
                                      )
                                    )
                                  }
                                  className={input_class}
                                />
                              </label>
                            </div>

                            <div className="grid gap-3 md:grid-cols-2">
                              <label className={label_class}>
                                <span>{t('serviceCatalog.fields.categoryFr')}</span>
                                <input
                                  type="text"
                                  value={item.category_fr || ''}
                                  onChange={event =>
                                    setItems(prev =>
                                      prev.map(existing =>
                                        existing.id === item.id
                                          ? { ...existing, category_fr: event.target.value || null }
                                          : existing
                                      )
                                    )
                                  }
                                  className={input_class}
                                />
                              </label>
                              <label className={label_class}>
                                <span>{t('serviceCatalog.fields.categoryEn')}</span>
                                <input
                                  type="text"
                                  value={item.category_en || ''}
                                  onChange={event =>
                                    setItems(prev =>
                                      prev.map(existing =>
                                        existing.id === item.id
                                          ? { ...existing, category_en: event.target.value || null }
                                          : existing
                                      )
                                    )
                                  }
                                  className={input_class}
                                />
                              </label>
                            </div>

                            <div className="grid gap-3 md:grid-cols-2">
                              <label className={label_class}>
                                <span>{t('serviceCatalog.fields.shortDescriptionFr')}</span>
                                <textarea
                                  value={item.short_description_fr || ''}
                                  onChange={event =>
                                    setItems(prev =>
                                      prev.map(existing =>
                                        existing.id === item.id
                                          ? {
                                              ...existing,
                                              short_description_fr: event.target.value || null,
                                            }
                                          : existing
                                      )
                                    )
                                  }
                                  rows={2}
                                  className={input_class}
                                />
                              </label>
                              <label className={label_class}>
                                <span>{t('serviceCatalog.fields.shortDescriptionEn')}</span>
                                <textarea
                                  value={item.short_description_en || ''}
                                  onChange={event =>
                                    setItems(prev =>
                                      prev.map(existing =>
                                        existing.id === item.id
                                          ? {
                                              ...existing,
                                              short_description_en: event.target.value || null,
                                            }
                                          : existing
                                      )
                                    )
                                  }
                                  rows={2}
                                  className={input_class}
                                />
                              </label>
                            </div>

                            <div className="grid gap-3 md:grid-cols-2">
                              <label className={label_class}>
                                <span>{t('serviceCatalog.fields.fullDescriptionFr')}</span>
                                <textarea
                                  value={item.full_description_fr || ''}
                                  onChange={event =>
                                    setItems(prev =>
                                      prev.map(existing =>
                                        existing.id === item.id
                                          ? {
                                              ...existing,
                                              full_description_fr: event.target.value || null,
                                            }
                                          : existing
                                      )
                                    )
                                  }
                                  rows={4}
                                  className={input_class}
                                />
                              </label>
                              <label className={label_class}>
                                <span>{t('serviceCatalog.fields.fullDescriptionEn')}</span>
                                <textarea
                                  value={item.full_description_en || ''}
                                  onChange={event =>
                                    setItems(prev =>
                                      prev.map(existing =>
                                        existing.id === item.id
                                          ? {
                                              ...existing,
                                              full_description_en: event.target.value || null,
                                            }
                                          : existing
                                      )
                                    )
                                  }
                                  rows={4}
                                  className={input_class}
                                />
                              </label>
                            </div>

                            <div className="grid gap-3 md:grid-cols-2">
                              <label className={label_class}>
                                <span>{t('serviceCatalog.fields.chatbotGreetingFr')}</span>
                                <input
                                  type="text"
                                  value={item.chatbot_greeting_fr || ''}
                                  onChange={event =>
                                    setItems(prev =>
                                      prev.map(existing =>
                                        existing.id === item.id
                                          ? {
                                              ...existing,
                                              chatbot_greeting_fr: event.target.value || null,
                                            }
                                          : existing
                                      )
                                    )
                                  }
                                  className={input_class}
                                />
                              </label>
                              <label className={label_class}>
                                <span>{t('serviceCatalog.fields.chatbotGreetingEn')}</span>
                                <input
                                  type="text"
                                  value={item.chatbot_greeting_en || ''}
                                  onChange={event =>
                                    setItems(prev =>
                                      prev.map(existing =>
                                        existing.id === item.id
                                          ? {
                                              ...existing,
                                              chatbot_greeting_en: event.target.value || null,
                                            }
                                          : existing
                                      )
                                    )
                                  }
                                  className={input_class}
                                />
                              </label>
                            </div>

                            <div className="grid gap-3 md:grid-cols-2">
                              <label className={label_class}>
                                <span>{t('serviceCatalog.fields.chatbotIntroFr')}</span>
                                <input
                                  type="text"
                                  value={item.chatbot_intro_fr || ''}
                                  onChange={event =>
                                    setItems(prev =>
                                      prev.map(existing =>
                                        existing.id === item.id
                                          ? {
                                              ...existing,
                                              chatbot_intro_fr: event.target.value || null,
                                            }
                                          : existing
                                      )
                                    )
                                  }
                                  className={input_class}
                                />
                              </label>
                              <label className={label_class}>
                                <span>{t('serviceCatalog.fields.chatbotIntroEn')}</span>
                                <input
                                  type="text"
                                  value={item.chatbot_intro_en || ''}
                                  onChange={event =>
                                    setItems(prev =>
                                      prev.map(existing =>
                                        existing.id === item.id
                                          ? {
                                              ...existing,
                                              chatbot_intro_en: event.target.value || null,
                                            }
                                          : existing
                                      )
                                    )
                                  }
                                  className={input_class}
                                />
                              </label>
                            </div>
                          </div>

                          <div className="flex flex-col gap-5 border-t border-gray-200 pt-5">
                            <div>
                              <h4 className="mb-2 text-sm font-semibold text-gray-900">
                                {t('serviceCatalog.fields.freelancerTypes')}
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {freelancerTypes.map(freelancerType => {
                                  const checked = freelancerTypeIds.includes(freelancerType.id);
                                  return (
                                    <button
                                      key={freelancerType.id}
                                      type="button"
                                      onClick={() =>
                                        void toggleItemFreelancerType(item.id, freelancerType.id)
                                      }
                                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
                                        checked
                                          ? 'border-primary bg-primary/10 text-primary'
                                          : 'border-gray-300 text-gray-600 hover:bg-white'
                                      }`}
                                    >
                                      {freelancerType.emoji} {freelancerType.name_fr}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            <div>
                              <div className="mb-2 flex items-center justify-between">
                                <h4 className="text-sm font-semibold text-gray-900">
                                  {t('serviceCatalog.fields.benefitTags')}
                                </h4>
                                <button
                                  type="button"
                                  onClick={() => void addBenefitTag(item.id)}
                                  className="inline-flex items-center gap-1 rounded-full border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-white"
                                >
                                  <Plus className="h-3 w-3" />
                                  {t('serviceCatalog.addBenefitTag')}
                                </button>
                              </div>
                              <div className="flex flex-col gap-2">
                                {benefitTags.map(tag => (
                                  <div key={tag.id} className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={tag.label_fr}
                                      placeholder={t('serviceCatalog.fields.labelFr')}
                                      onChange={event =>
                                        setItemBenefitTags(prev => ({
                                          ...prev,
                                          [item.id]: (prev[item.id] ?? []).map(existing =>
                                            existing.id === tag.id
                                              ? { ...existing, label_fr: event.target.value }
                                              : existing
                                          ),
                                        }))
                                      }
                                      className={`${input_class} flex-1`}
                                    />
                                    <input
                                      type="text"
                                      value={tag.label_en}
                                      placeholder={t('serviceCatalog.fields.labelEn')}
                                      onChange={event =>
                                        setItemBenefitTags(prev => ({
                                          ...prev,
                                          [item.id]: (prev[item.id] ?? []).map(existing =>
                                            existing.id === tag.id
                                              ? { ...existing, label_en: event.target.value }
                                              : existing
                                          ),
                                        }))
                                      }
                                      className={`${input_class} flex-1`}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => void deleteBenefitTag(item.id, tag.id)}
                                      className="rounded-full border border-red-200 p-2 text-red-600 hover:bg-red-50"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <div>
                              <div className="mb-2 flex items-center justify-between">
                                <h4 className="text-sm font-semibold text-gray-900">
                                  {t('serviceCatalog.fields.chatbotQuestions')}
                                </h4>
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() => void addChatbotQuestion(item.id, 'text')}
                                    className="rounded-full border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-white"
                                  >
                                    + {t('serviceCatalog.stepTypes.text')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => void addChatbotQuestion(item.id, 'choice')}
                                    className="rounded-full border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-white"
                                  >
                                    + {t('serviceCatalog.stepTypes.choice')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => void addChatbotQuestion(item.id, 'contact')}
                                    className="rounded-full border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-white"
                                  >
                                    + {t('serviceCatalog.stepTypes.contact')}
                                  </button>
                                </div>
                              </div>

                              <div className="flex flex-col gap-3">
                                {chatbotQuestions.map((question, qIndex) => (
                                  <div
                                    key={question.id}
                                    className="rounded-2xl border border-gray-200 bg-white p-3"
                                  >
                                    <div className="mb-2 flex items-center justify-between">
                                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {t(`serviceCatalog.stepTypes.${question.step_type}`)} · #
                                        {qIndex + 1}
                                      </span>
                                      <div className="flex items-center gap-1">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            void moveChatbotQuestion(item.id, qIndex, -1)
                                          }
                                          disabled={qIndex === 0 || isDeletingQuestion}
                                          className="rounded-full border border-gray-300 p-1.5 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                                        >
                                          <ArrowUp className="h-3 w-3" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            void moveChatbotQuestion(item.id, qIndex, 1)
                                          }
                                          disabled={
                                            qIndex === chatbotQuestions.length - 1 ||
                                            isDeletingQuestion
                                          }
                                          className="rounded-full border border-gray-300 p-1.5 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                                        >
                                          <ArrowDown className="h-3 w-3" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            void deleteChatbotQuestion(item.id, question.id)
                                          }
                                          disabled={isDeletingQuestion}
                                          aria-label={t('serviceCatalog.deleteQuestion')}
                                          className="rounded-full border border-red-200 p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-40"
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </button>
                                      </div>
                                    </div>

                                    {question.step_type !== 'contact' && (
                                      <div className="grid gap-2 md:grid-cols-2">
                                        <input
                                          type="text"
                                          value={question.question_text_fr || ''}
                                          placeholder={t('serviceCatalog.fields.questionTextFr')}
                                          onChange={event =>
                                            setItemChatbotQuestions(prev => ({
                                              ...prev,
                                              [item.id]: (prev[item.id] ?? []).map(existing =>
                                                existing.id === question.id
                                                  ? {
                                                      ...existing,
                                                      question_text_fr: event.target.value,
                                                    }
                                                  : existing
                                              ),
                                            }))
                                          }
                                          className={input_class}
                                        />
                                        <input
                                          type="text"
                                          value={question.question_text_en || ''}
                                          placeholder={t('serviceCatalog.fields.questionTextEn')}
                                          onChange={event =>
                                            setItemChatbotQuestions(prev => ({
                                              ...prev,
                                              [item.id]: (prev[item.id] ?? []).map(existing =>
                                                existing.id === question.id
                                                  ? {
                                                      ...existing,
                                                      question_text_en: event.target.value,
                                                    }
                                                  : existing
                                              ),
                                            }))
                                          }
                                          className={input_class}
                                        />
                                      </div>
                                    )}

                                    {question.step_type === 'text' && (
                                      <div className="mt-2 grid gap-2 md:grid-cols-2">
                                        <input
                                          type="text"
                                          value={question.placeholder_fr || ''}
                                          placeholder={t('serviceCatalog.fields.placeholderFr')}
                                          onChange={event =>
                                            setItemChatbotQuestions(prev => ({
                                              ...prev,
                                              [item.id]: (prev[item.id] ?? []).map(existing =>
                                                existing.id === question.id
                                                  ? {
                                                      ...existing,
                                                      placeholder_fr: event.target.value || null,
                                                    }
                                                  : existing
                                              ),
                                            }))
                                          }
                                          className={input_class}
                                        />
                                        <input
                                          type="text"
                                          value={question.placeholder_en || ''}
                                          placeholder={t('serviceCatalog.fields.placeholderEn')}
                                          onChange={event =>
                                            setItemChatbotQuestions(prev => ({
                                              ...prev,
                                              [item.id]: (prev[item.id] ?? []).map(existing =>
                                                existing.id === question.id
                                                  ? {
                                                      ...existing,
                                                      placeholder_en: event.target.value || null,
                                                    }
                                                  : existing
                                              ),
                                            }))
                                          }
                                          className={input_class}
                                        />
                                      </div>
                                    )}

                                    {question.step_type === 'choice' && (
                                      <div className="mt-2 flex flex-col gap-2">
                                        {(question.options ?? []).map((option, optIndex) => (
                                          <div key={optIndex} className="flex items-center gap-2">
                                            <input
                                              type="text"
                                              value={option.label_fr}
                                              placeholder={t('serviceCatalog.fields.labelFr')}
                                              onChange={event => {
                                                const nextOptions = [...(question.options ?? [])];
                                                nextOptions[optIndex] = {
                                                  ...option,
                                                  label_fr: event.target.value,
                                                };
                                                updateQuestionOptions(
                                                  item.id,
                                                  question.id,
                                                  nextOptions
                                                );
                                              }}
                                              className={`${input_class} flex-1`}
                                            />
                                            <input
                                              type="text"
                                              value={option.label_en}
                                              placeholder={t('serviceCatalog.fields.labelEn')}
                                              onChange={event => {
                                                const nextOptions = [...(question.options ?? [])];
                                                nextOptions[optIndex] = {
                                                  ...option,
                                                  label_en: event.target.value,
                                                };
                                                updateQuestionOptions(
                                                  item.id,
                                                  question.id,
                                                  nextOptions
                                                );
                                              }}
                                              className={`${input_class} flex-1`}
                                            />
                                            <select
                                              value={option.goto || ''}
                                              onChange={event => {
                                                const nextOptions = [...(question.options ?? [])];
                                                nextOptions[optIndex] = {
                                                  ...option,
                                                  goto: event.target.value || null,
                                                };
                                                updateQuestionOptions(
                                                  item.id,
                                                  question.id,
                                                  nextOptions
                                                );
                                              }}
                                              className={input_class}
                                            >
                                              <option value="">
                                                {t('serviceCatalog.fields.gotoNext')}
                                              </option>
                                              {chatbotQuestions
                                                .filter(q => q.id !== question.id)
                                                .map(q => (
                                                  <option key={q.client_key} value={q.client_key}>
                                                    {q.question_text_fr || q.client_key}
                                                  </option>
                                                ))}
                                            </select>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const nextOptions = (question.options ?? []).filter(
                                                  (_, i) => i !== optIndex
                                                );
                                                updateQuestionOptions(
                                                  item.id,
                                                  question.id,
                                                  nextOptions
                                                );
                                              }}
                                              className="rounded-full border border-red-200 p-2 text-red-600 hover:bg-red-50"
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </button>
                                          </div>
                                        ))}
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const nextOptions = [
                                              ...(question.options ?? []),
                                              {
                                                value: `option-${(question.options?.length ?? 0) + 1}`,
                                                label_fr: '',
                                                label_en: '',
                                                goto: null,
                                              },
                                            ];
                                            updateQuestionOptions(
                                              item.id,
                                              question.id,
                                              nextOptions
                                            );
                                          }}
                                          className="self-start rounded-full border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-white"
                                        >
                                          <Plus className="mr-1 inline h-3 w-3" />
                                          {t('serviceCatalog.addOption')}
                                        </button>
                                      </div>
                                    )}

                                    <label className="mt-2 inline-flex items-center gap-2 text-xs font-medium text-gray-700">
                                      <input
                                        type="checkbox"
                                        checked={question.optional}
                                        onChange={event =>
                                          setItemChatbotQuestions(prev => ({
                                            ...prev,
                                            [item.id]: (prev[item.id] ?? []).map(existing =>
                                              existing.id === question.id
                                                ? { ...existing, optional: event.target.checked }
                                                : existing
                                            ),
                                          }))
                                        }
                                        className="h-3.5 w-3.5 rounded border-gray-300"
                                      />
                                      {t('serviceCatalog.fields.optional')}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <div className="flex justify-end border-t border-gray-200 pt-4">
                              <button
                                type="button"
                                onClick={() => void saveAllForItem(item)}
                                disabled={isSaving}
                                className="rounded-full bg-secondary px-5 py-2.5 text-sm font-semibold text-white hover:bg-secondary/90 disabled:opacity-50"
                              >
                                {isSaving ? t('serviceCatalog.saving') : t('serviceCatalog.save')}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          {activeTab === 'freelancerTypes' && (
            <section className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm">
              <div className="mb-5 flex items-center justify-between gap-4">
                <h2 className="text-2xl font-semibold text-gray-900">
                  {t('serviceCatalog.tabs.freelancerTypes')}
                </h2>
                <button
                  type="button"
                  onClick={() => void handleAddFreelancerType()}
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
                  disabled={Boolean(savingKeys['freelancerType:add'])}
                >
                  {savingKeys['freelancerType:add'] ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  {t('serviceCatalog.addFreelancerType')}
                </button>
              </div>

              <div className="flex flex-col gap-3">
                {freelancerTypes.map((item, index) => {
                  const isSavingType = Boolean(savingKeys[`freelancerType:${item.id}`]);

                  return (
                    <div
                      key={item.id}
                      className="flex flex-wrap items-center gap-2 rounded-2xl border border-gray-200 bg-gray-50/60 p-3"
                    >
                      <input
                        type="text"
                        value={item.emoji}
                        onChange={event =>
                          setFreelancerTypes(prev =>
                            prev.map(existing =>
                              existing.id === item.id
                                ? { ...existing, emoji: event.target.value }
                                : existing
                            )
                          )
                        }
                        className={`${input_class} w-16 text-center`}
                      />
                      <input
                        type="text"
                        value={item.name_fr}
                        placeholder={t('serviceCatalog.fields.nameFr')}
                        onChange={event =>
                          setFreelancerTypes(prev =>
                            prev.map(existing =>
                              existing.id === item.id
                                ? { ...existing, name_fr: event.target.value }
                                : existing
                            )
                          )
                        }
                        className={`${input_class} flex-1`}
                      />
                      <input
                        type="text"
                        value={item.name_en}
                        placeholder={t('serviceCatalog.fields.nameEn')}
                        onChange={event =>
                          setFreelancerTypes(prev =>
                            prev.map(existing =>
                              existing.id === item.id
                                ? { ...existing, name_en: event.target.value }
                                : existing
                            )
                          )
                        }
                        className={`${input_class} flex-1`}
                      />
                      <input
                        type="color"
                        value={item.color}
                        onChange={event =>
                          setFreelancerTypes(prev =>
                            prev.map(existing =>
                              existing.id === item.id
                                ? { ...existing, color: event.target.value }
                                : existing
                            )
                          )
                        }
                        className="h-10 w-10 rounded border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => void moveFreelancerType(index, -1)}
                        disabled={index === 0}
                        className="rounded-full border border-gray-300 p-2 text-gray-600 hover:bg-white disabled:opacity-40"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void moveFreelancerType(index, 1)}
                        disabled={index === freelancerTypes.length - 1}
                        className="rounded-full border border-gray-300 p-2 text-gray-600 hover:bg-white disabled:opacity-40"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void saveFreelancerType(item)}
                        disabled={isSavingType}
                        className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-2 text-sm font-semibold text-white hover:bg-secondary/90 disabled:opacity-60"
                      >
                        {isSavingType && <Loader2 className="h-4 w-4 animate-spin" />}
                        {t('serviceCatalog.save')}
                      </button>
                      <button
                        type="button"
                        onClick={() => void deleteFreelancerType(item.id)}
                        className="rounded-full border border-red-200 p-2 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {activeTab === 'leads' && (
            <section className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="mb-5 text-2xl font-semibold text-gray-900">
                {t('serviceCatalog.tabs.leads')}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                      <th className="py-2 pr-4">{t('serviceCatalog.fields.createdAt')}</th>
                      <th className="py-2 pr-4">{t('serviceCatalog.fields.serviceName')}</th>
                      <th className="py-2 pr-4">{t('serviceCatalog.fields.email')}</th>
                      <th className="py-2 pr-4">{t('serviceCatalog.fields.phone')}</th>
                      <th className="py-2 pr-4">{t('serviceCatalog.fields.locale')}</th>
                      <th className="py-2 pr-4">{t('serviceCatalog.fields.status')}</th>
                      <th className="py-2 pr-4">{t('serviceCatalog.fields.answers')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map(lead => (
                      <tr key={lead.id} className="border-b border-gray-100 align-top">
                        <td className="py-3 pr-4 whitespace-nowrap text-gray-500">
                          {new Date(lead.created_at).toLocaleString()}
                        </td>
                        <td className="py-3 pr-4">{lead.service_name_snapshot || '—'}</td>
                        <td className="py-3 pr-4">{lead.email}</td>
                        <td className="py-3 pr-4">{lead.phone || '—'}</td>
                        <td className="py-3 pr-4 uppercase">{lead.locale}</td>
                        <td className="py-3 pr-4">
                          <select
                            value={lead.status}
                            onChange={event =>
                              void changeLeadStatus(
                                lead,
                                event.target.value as ServiceCatalogLeadStatus
                              )
                            }
                            className="rounded-xl border border-gray-300 px-2 py-1.5 text-sm"
                          >
                            <option value="new">{t('serviceCatalog.leadStatus.new')}</option>
                            <option value="contacted">
                              {t('serviceCatalog.leadStatus.contacted')}
                            </option>
                            <option value="closed">{t('serviceCatalog.leadStatus.closed')}</option>
                          </select>
                        </td>
                        <td className="py-3 pr-4 max-w-xs">
                          <ul className="flex flex-col gap-1 text-xs text-gray-600">
                            {lead.answers.map((answer, i) => (
                              <li key={i}>
                                <span className="font-semibold">{answer.question}:</span>{' '}
                                {answer.answer}
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {leads.length === 0 && (
                  <p className="py-6 text-center text-sm text-gray-500">
                    {t('serviceCatalog.noLeads')}
                  </p>
                )}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
